/* Presentation only: the real run and a loaded cart frame gate the reveal. */
(() => {
  const root = document.querySelector('[data-cvt]');
  if (!root) return;
  const stage = root.querySelector('[data-cvt-view="run"]');
  const device = root.querySelector('[data-cvt-device]');
  const screen = root.querySelector('.cvt-device__screen');
  const frame = root.querySelector('[data-cvt-frame]');
  const store = root.querySelector('[data-cvt-store-img]');
  const placeholder = root.querySelector('[data-cvt-placeholder]');
  const host = root.querySelector('[data-cvt-crew]');
  const title = root.querySelector('.cvt-wait-title');
  const cancel = root.querySelector('[data-cvt-cancel]');
  const retry = root.querySelector('[data-cvt-retry-frame]');
  if (!stage || !device || !screen || !frame || !store || !title || !cancel) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 1199px)');
  let crew, active = false, done = false, revealing = false, serial = 0;
  let source = '', frameLoaded = false, storeSeen = false, scene = 'read';
  let frameTimer, revealTimer, watchdog, measureFrame;
  const originalTabIndex = frame.getAttribute('tabindex');
  stage.tabIndex = -1;

  function ensureCrew() {
    if (compact.matches || reduce.matches) return null;
    if (!crew && host && window.CartivatePreviewCrew) {
      crew = window.CartivatePreviewCrew.create(host);
    }
    if (crew) root.classList.add('cvt--crew-enabled');
    return crew;
  }
  function drawerRect() {
    const box = screen.getBoundingClientRect();
    const cart = (root.querySelector('[data-cvt-cart-layer]') || frame).getBoundingClientRect();
    return {x:cart.left - box.left, y:cart.top - box.top, width:cart.width, height:cart.height};
  }
  function measure() {
    if (measureFrame) return;
    measureFrame = requestAnimationFrame(() => {
      measureFrame = null;
      if (active) crew?.setDrawer(drawerRect());
    });
  }
  function protectDraft() {
    frame.inert = true;
    frame.tabIndex = -1;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('aria-busy', 'true');
    device.inert = false;
    device.removeAttribute('aria-hidden');
  }
  function enableCart() {
    frame.inert = false;
    frame.removeAttribute('aria-hidden');
    frame.removeAttribute('aria-busy');
    if (originalTabIndex === null) frame.removeAttribute('tabindex');
    else frame.setAttribute('tabindex', originalTabIndex);
  }
  function updateSource() {
    const next = frame.getAttribute('src') || '';
    if (next !== source) { source = next; frameLoaded = false; }
  }
  function readScene(progress) {
    for (const name of ['review', 'design', 'products']) {
      if (['active','done','complete','completed'].includes(progress[name])) return name;
    }
    return 'read';
  }
  function updatePresentation(detail = {}) {
    updateSource();
    if (store.complete && store.naturalWidth > 0 && (store.currentSrc || store.getAttribute('src'))) storeSeen = true;
    root.dataset.cvtStoreReady = String(storeSeen);
    const phase = storeSeen || (!frame.hidden && frameLoaded) ? 'work' : 'arrival';
    if (active && placeholder) placeholder.hidden = phase === 'work';
    if (active && !revealing && root.dataset.previewPhase !== 'frame-delayed') {
      root.dataset.previewPhase = phase === 'work' ? 'working' : 'arrival';
    }
    if (detail.progress && !done) {
      const next = readScene(detail.progress);
      if (next !== scene) { scene = next; crew?.setScene(scene); }
    }
    crew?.setPhase(phase);
    crew?.setDrawer(drawerRect());
  }
  function clearTimers() {
    clearTimeout(frameTimer); clearTimeout(revealTimer); clearTimeout(watchdog);
    frameTimer = revealTimer = watchdog = null;
  }
  function clear() {
    serial++;
    clearTimers();
    cancelAnimationFrame(measureFrame); measureFrame = null;
    crew?.stop();
    active = done = revealing = false;
    root.classList.remove('cvt--waiting', 'cvt--revealing', 'cvt--drawer-unveiled');
    title.hidden = cancel.hidden = true;
    if (retry) retry.hidden = true;
    device.inert = false; device.removeAttribute('aria-hidden');
    enableCart();
  }
  function begin() {
    clear();
    active = true;
    source = ''; frameLoaded = storeSeen = false; scene = 'read';
    root.dataset.previewPhase = 'arrival';
    root.dataset.cvtStoreReady = 'false';
    root.classList.add('cvt--waiting');
    title.hidden = cancel.hidden = false;
    protectDraft();
    ensureCrew()?.start();
    crew?.setScene(scene);
    updatePresentation();
    stage.scrollIntoView({behavior:reduce.matches ? 'instant' : 'smooth', block:'start'});
    stage.focus({preventScroll:true});
    const epoch = serial;
    watchdog = setTimeout(() => {
      if (epoch !== serial || !active || done) return;
      root.querySelector('[data-cvt-narration]').textContent = 'Still creating your cart. You can explore your website while the crew keeps working.';
    }, 300000);
  }
  function unveil() {
    if (!active || !done || !revealing) return;
    root.classList.add('cvt--drawer-unveiled');
    // Keep the covered cart out of keyboard/pointer interaction until the curtain clears.
  }
  function finish(epoch) {
    if (epoch !== serial || !active || !done) return;
    const anchor = device.getBoundingClientRect();
    const keepInView = anchor.bottom > 0 && anchor.top < innerHeight;
    unveil();
    clearTimers();
    crew?.finish();
    root.classList.remove('cvt--waiting', 'cvt--revealing');
    root.classList.add('cvt--drawer-unveiled');
    root.dataset.previewPhase = 'ready';
    active = revealing = false;
    title.hidden = cancel.hidden = true;
    if (retry) retry.hidden = true;
    enableCart();
    // Existing result controls change the grid height. Keep the cart in place as they appear.
    if (keepInView) {
      const shift = device.getBoundingClientRect().top - anchor.top;
      if (Math.abs(shift) > 1) window.scrollBy({top:shift, behavior:'instant'});
    }
    root.dispatchEvent(new CustomEvent('cartivate:preview-presented'));
  }
  function reveal() {
    if (!active || !done || revealing || root.dataset.previewPhase === 'ready') return;
    updateSource();
    if (!source || !frameLoaded || frame.hidden) return;
    revealing = true;
    clearTimers();
    root.classList.add('cvt--revealing');
    root.dataset.previewPhase = 'revealing';
    if (retry) retry.hidden = true;
    const epoch = serial;
    const box = screen.getBoundingClientRect();
    if (reduce.matches || compact.matches || document.hidden || box.bottom <= 0 || box.top >= innerHeight || !ensureCrew()) {
      unveil(); finish(epoch); return;
    }
    crew.setDrawer(drawerRect());
    crew.pause(false);
    revealTimer = setTimeout(() => finish(epoch), (window.CartivatePreviewCrew?.timings?.duration || 8200) + 1200);
    Promise.resolve(crew.reveal()).then(() => finish(epoch), () => finish(epoch));
  }
  function waitForFrame() {
    if (frameTimer || !active || !done || frameLoaded || revealing) return;
    const epoch = serial;
    frameTimer = setTimeout(() => {
      frameTimer = null;
      if (epoch !== serial || !active || !done || frameLoaded) return;
      root.dataset.previewPhase = 'frame-delayed';
      root.querySelector('[data-cvt-narration]').textContent = 'Your design is ready. The interactive cart is still loading. You can reload the cart preview or keep exploring your website.';
      if (retry) retry.hidden = false;
    }, 12000);
  }
  root.addEventListener('cartivate:preview-start', begin);
  root.addEventListener('cartivate:preview-progress', event => {
    if (!active || revealing) return;
    crew?.pause(false);
    updatePresentation(event.detail);
  });
  root.addEventListener('cartivate:preview-ready', event => {
    if (!active || revealing || root.dataset.previewPhase === 'ready') return;
    done = true;
    updatePresentation(event.detail);
    reveal(); waitForFrame();
  });
  host?.addEventListener('cartivate:crew-unveil', unveil);
  store.addEventListener('load', () => {
    if (!active || revealing || !store.naturalWidth) return;
    storeSeen = true;
    updatePresentation();
  });
  frame.addEventListener('load', () => {
    const current = frame.getAttribute('src');
    if (!current || frame.hidden) return;
    source = current; frameLoaded = true;
    if (active && !revealing) { updatePresentation(); reveal(); }
  });
  frame.addEventListener('error', () => {
    if (!active || revealing) return;
    frameLoaded = false;
    root.dataset.previewPhase = 'frame-delayed';
    protectDraft();
    if (retry) retry.hidden = false;
  });
  root.addEventListener('cartivate:preview-connection-lost', () => {
    if (!active || revealing) return;
    root.dataset.previewPhase = 'reconnecting';
    protectDraft();
    crew?.pause(true);
  });
  for (const type of ['error', 'reset']) root.addEventListener(`cartivate:preview-${type}`, event => {
    if (type === 'error' && event.detail?.preserveSession) return;
    clear();
    root.dataset.previewPhase = type === 'reset' ? 'form' : 'error';
  });
  retry?.addEventListener('click', () => {
    if (!active || !source) return;
    frameLoaded = false; retry.hidden = true;
    root.dataset.previewPhase = storeSeen ? 'working' : 'arrival';
    frame.src = source;
    waitForFrame();
  });
  cancel.addEventListener('click', () => root.querySelector('[data-cvt-again]')?.click());
  document.addEventListener('keydown', event => {
    if (active && event.key === 'Escape' && root.contains(document.activeElement)) {
      event.preventDefault(); cancel.click();
    }
  });
  function motionMode() {
    if (compact.matches || reduce.matches) {
      if (revealing) finish(serial);
      else crew?.stop();
      root.classList.remove('cvt--crew-enabled');
    } else if (active && !revealing) {
      ensureCrew()?.start();
      crew?.setScene(scene);
      updatePresentation();
    }
  }
  reduce.addEventListener('change', motionMode);
  compact.addEventListener('change', motionMode);
  if (typeof ResizeObserver === 'function') new ResizeObserver(measure).observe(screen);
  window.addEventListener('resize', measure);
  // BFCache keeps the live session object. Suspend decoration without exposing a draft.
  window.addEventListener('pagehide', () => {
    crew?.pause(true);
    if (revealing) finish(serial);
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted || !active) return;
    crew?.pause(false);
    updatePresentation();
    if (done) { reveal(); waitForFrame(); }
  });
})();
